'use client';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Section, HubItem, DocumentHit } from '@workspace/interfaces';
import { AccordionWithCustomContent } from '@workspace/ui/common/AccordionWithCustomContent';
import { DocumentHubItem } from '@workspace/widgets';
import { Configure, Hits, Index, useClearRefinements } from 'react-instantsearch';
import { ALGOLIA_CONFIG } from '@/app/lib';
import { Hit as AlgoliaHit } from 'instantsearch.js/es/types';
import { AlgoliaShowMore, NoResultsBoundary } from '@workspace/widgets';
import { Typography } from '@workspace/ui/common';

interface DocumentHubProps {
  sections: Section[];
  className?: string;
  filter?: (item: HubItem, section: Section) => boolean;
  pageSlug?: string;
  filterActive?: boolean;
  onTagsDiscovered?: (tags: { id: string; label: string }[]) => void;
  downloadCTA?: string;
  viewCTA?: string;
  selectedTagIds?: string[];
}

const HitComponent = ({
  hit,
  downloadCTA,
  viewCTA,
}: {
  hit: AlgoliaHit<DocumentHit>;
  downloadCTA?: string;
  viewCTA?: string;
}) => {
  const item = useMemo<HubItem>(() => {
    return {
      id: hit.objectID,
      title: hit.title,
      image: hit.thumbnailImage ?? null,
      filename: hit.title,
      fileUrl: hit.fileUrl || '',
      tags: hit.documentTags?.map((tag) => tag.name) || [],
    };
  }, [hit]);
  return <DocumentHubItem item={item} downloadCTA={downloadCTA} viewCTA={viewCTA} />;
};

const SectionIndex = React.memo(function SectionIndex({
  indexId,
  filterQuery,
  downloadCTA,
  viewCTA,
}: {
  indexId: string;
  filterQuery: string;
  downloadCTA?: string;
  viewCTA?: string;
}) {
  const BoundHitComponentCallback = React.useCallback(
    ({ hit }: { hit: AlgoliaHit<DocumentHit> }) => (
      <HitComponent hit={hit} downloadCTA={downloadCTA} viewCTA={viewCTA} />
    ),
    [downloadCTA, viewCTA],
  );

  return (
    <Index indexName={ALGOLIA_CONFIG.LIST_INSTANT_SEARCH_INDEX.Documents} indexId={indexId}>
      <Configure filters={filterQuery} />
      <NoResultsBoundary
        fallback={
          <Typography className="text-center" variant="bodyMd">
            No documents available in this section
          </Typography>
        }
      >
        <Hits<DocumentHit>
          hitComponent={BoundHitComponentCallback}
          classNames={{
            list: 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 xl:gap-x-5 xl:gap-y-6 gap-4',
          }}
        />
        <AlgoliaShowMore />
      </NoResultsBoundary>
    </Index>
  );
});

const constructFilterQuery = (
  autoFilter: Section['autoFilter'],
  additionalFilters: string[],
  manualFiles?: Section['documentFiles'],
) => {
  const escapeValue = (v: string) => v?.replace(/"/g, '\\"');

  // build manual files clause: (fileId:"id1" OR fileId:"id2")
  let manualFilesQuery = '';
  if (manualFiles && manualFiles.length > 0) {
    const parts = manualFiles
      .map((file, idx) => {
        const score = manualFiles.length - idx; // higher score for earlier files
        if (!file._ref) return '';
        return `fileId:"${escapeValue(file._ref)}"<score=${score}>`;
      })
      .filter(Boolean);
    manualFilesQuery = parts.length > 1 ? `(${parts.join(' OR ')})` : parts[0];
  }

  // build autoFilter clause. For each key we OR multiple values for that key like
  // (documentTags:"A" OR documentTags:"B"). Different keys are combined with OR.
  let autoFilterQuery = '';
  if (autoFilter) {
    const clauses: string[] = [];
    Object.entries(autoFilter).forEach(([key, values]) => {
      if (!values || values.length === 0) return;
      values.forEach((v) => {
        const val = v?.name;
        if (!val) return;
        clauses.push(`${key}.name:"${escapeValue(val)}"`);
      });
    });
    if (clauses.length === 1) autoFilterQuery = clauses[0];
    else if (clauses.length > 1) autoFilterQuery = `(${clauses.join(' OR ')})`;
  }

  // additionalFilters should be ORed together. Use this for
  // selectedTagIds; adapt by assuming these map to documentTags unless they already
  // contain a colon (e.g., productGroups:"X").
  let additionalFilterQuery = '';
  if (additionalFilters && additionalFilters.length > 0) {
    const parts = additionalFilters
      .map((tag) => {
        const containsField = /\w+\s*:\s*"?.+"?/; // crude check if already fielded
        if (containsField.test(tag)) return tag;
        return `documentTags.name:"${escapeValue(tag)}"`;
      })
      .filter(Boolean);
    additionalFilterQuery = parts.length > 1 ? `(${parts.join(' OR ')})` : parts[0] || '';
  }

  // Combine manualFilesQuery and autoFilterQuery with OR (they are alternate sources),
  // then AND with additionalFilterQuery when present.
  const leftSideParts = [manualFilesQuery, autoFilterQuery].filter(Boolean);
  const left =
    leftSideParts.length === 0 ? '' : leftSideParts.length === 1 ? leftSideParts[0] : `(${leftSideParts.join(' OR ')})`;

  if (left && additionalFilterQuery) return `${left} AND ${additionalFilterQuery}`;
  return left || additionalFilterQuery || '';
};

export function AlgoliaDocumentHub({
  sections,
  className,
  selectedTagIds,
  filterActive,
  downloadCTA,
  viewCTA,
}: DocumentHubProps) {
  const { canRefine } = useClearRefinements({ excludedAttributes: [] });
  const accordionSections = useMemo(() => {
    return sections.map((section) => {
      const filterQuery = constructFilterQuery(
        section.autoFilter,
        filterActive ? selectedTagIds || [] : [],
        section.documentFiles,
      );

      return {
        id: section.id,
        title: section.title,
        content: (
          <SectionIndex indexId={section.id} filterQuery={filterQuery} downloadCTA={downloadCTA} viewCTA={viewCTA} />
        ),
      };
    });
  }, [downloadCTA, filterActive, sections, selectedTagIds, viewCTA]);

  const normalizedIds = useMemo(() => {
    const seen = new Map<string, number>();
    return (sections || []).map((s, i) => {
      const count = (seen.get(s.id) ?? 0) + 1;
      seen.set(s.id, count);
      const __kid = count === 1 ? s.id : `${s.id}__${i}`;
      return __kid;
    });
  }, [sections]);

  const [openIds, setOpenIds] = useState<string[]>(normalizedIds);

  const prevFilterRef = useRef({
    selectedTagIds,
    filterActive,
    canRefine,
  });

  useEffect(() => {
    const filterChanged =
      prevFilterRef.current.selectedTagIds !== selectedTagIds ||
      prevFilterRef.current.filterActive !== filterActive ||
      prevFilterRef.current.canRefine !== canRefine;

    if (filterChanged) {
      setOpenIds(normalizedIds);
      prevFilterRef.current = { selectedTagIds, filterActive, canRefine };
    }
  }, [selectedTagIds, filterActive, canRefine, normalizedIds]);

  return (
    <AccordionWithCustomContent
      sections={accordionSections}
      allowMultiple
      className={className}
      openIds={openIds}
      onChange={setOpenIds}
    />
  );
}
